import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { GlobalMusicPlayer } from "@/components/GlobalMusicPlayer";
import StarterCreditsWelcome from "@/components/StarterCreditsWelcome";
import NotFound from "@/pages/NotFound";
import { Redirect, Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AudioPlayerProvider } from "@/contexts/AudioPlayerContext";
import Layout from "./components/Layout";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { supportedLanguages } from "./locales";
import { useTranslation } from "react-i18next";
import { useEffect, lazy, Suspense, memo, useState } from "react";
import SignupSurveyModal from "./components/SignupSurveyModal";
import MobileAppPrompt from "@/components/MobileAppPrompt";
import posthog from "posthog-js";
import { captureGclidFromUrl, syncPostHogAttribution } from "@/lib/attribution";
import { useBuildVersionReload } from "@/hooks/useBuildVersionReload";
import { useAuth } from "@/_core/hooks/useAuth";

// The homepage is the highest-traffic route, so its chunk is warmed
// immediately (in parallel with app init) instead of after React has mounted
// and resolved the route — that serial hop was 1-2 s of LCP on 4G phones.
// server/_core/routePreloads.ts additionally emits modulepreload hints for it
// straight from the HTML. The route "/" renders LandingV2 (the 2026 landing);
// the previous Landing is no longer routed anywhere, so warming it just put
// ~500 KB of unused JS on the critical path.
const LandingV2 = lazy(() => import("./pages/LandingV2"));
if (typeof window !== "undefined") {
  const segments = window.location.pathname.split("/").filter(Boolean);
  const isHomeRoute =
    segments.length === 0 ||
    (segments.length === 1 &&
      supportedLanguages.includes(segments[0] as (typeof supportedLanguages)[number]));
  if (isHomeRoute) void import("./pages/LandingV2");
}

// All other pages loaded lazily for better initial bundle size
const Home = lazy(() => import("./pages/Home"));
const AICover = lazy(() => import("./pages/AICover"));
const VoiceCoverCreate = lazy(() => import("./pages/VoiceCoverCreate"));
const VocalRemover = lazy(() => import("./pages/VocalRemover"));
const StemSplitter = lazy(() => import("./pages/StemSplitter"));
const SongDetail = lazy(() => import("./pages/SongDetail"));
const AuthorDetail = lazy(() => import("./pages/AuthorDetail"));
const Notifications = lazy(() => import("./pages/Notifications"));
const CliAuth = lazy(() => import("./pages/CliAuth"));
const CliLanding = lazy(() => import("./pages/CliLanding"));
const SkillLanding = lazy(() => import("./pages/SkillLanding"));
const McpLanding = lazy(() => import("./pages/McpLanding"));
const McpAuth = lazy(() => import("./pages/McpAuth"));
const CoverDetail = lazy(() => import("./pages/CoverDetail"));
const VideoDetail = lazy(() => import("./pages/VideoDetail"));
const Songs = lazy(() => import("./pages/Songs"));
const Featured = lazy(() => import("./pages/Featured"));
const Subscription = lazy(() => import("./pages/Subscription"));
const Playlists = lazy(() => import("./pages/Playlists"));
const PlaylistDetail = lazy(() => import("./pages/PlaylistDetail"));
const ChristmasSongGenerator = lazy(
  () => import("./pages/genres/ChristmasSongGenerator"),
);
const CountrySongGenerator = lazy(
  () => import("./pages/genres/CountrySongGenerator"),
);
const MetalSongGenerator = lazy(
  () => import("./pages/genres/MetalSongGenerator"),
);
const LoveSongGenerator = lazy(
  () => import("./pages/genres/LoveSongGenerator"),
);
const KpopSongGenerator = lazy(
  () => import("./pages/genres/KpopSongGenerator"),
);
const LofiSongGenerator = lazy(
  () => import("./pages/genres/LofiSongGenerator"),
);
const RapSongGenerator = lazy(() => import("./pages/genres/RapSongGenerator"));
// Genre pages
const PopSongGenerator = lazy(() => import("./pages/genres/PopSongGenerator"));
const RockSongGenerator = lazy(
  () => import("./pages/genres/RockSongGenerator"),
);
const JazzSongGenerator = lazy(
  () => import("./pages/genres/JazzSongGenerator"),
);
const BluesSongGenerator = lazy(
  () => import("./pages/genres/BluesSongGenerator"),
);
const RnbSongGenerator = lazy(() => import("./pages/genres/RnbSongGenerator"));
const HipHopSongGenerator = lazy(
  () => import("./pages/genres/HipHopSongGenerator"),
);
const EdmSongGenerator = lazy(() => import("./pages/genres/EdmSongGenerator"));
const IndieSongGenerator = lazy(
  () => import("./pages/genres/IndieSongGenerator"),
);
const FolkSongGenerator = lazy(
  () => import("./pages/genres/FolkSongGenerator"),
);
const ReggaeSongGenerator = lazy(
  () => import("./pages/genres/ReggaeSongGenerator"),
);
const GospelSongGenerator = lazy(
  () => import("./pages/genres/GospelSongGenerator"),
);
const SoulSongGenerator = lazy(
  () => import("./pages/genres/SoulSongGenerator"),
);
const FunkSongGenerator = lazy(
  () => import("./pages/genres/FunkSongGenerator"),
);
const PunkSongGenerator = lazy(
  () => import("./pages/genres/PunkSongGenerator"),
);
const LatinSongGenerator = lazy(
  () => import("./pages/genres/LatinSongGenerator"),
);
const AfrobeatSongGenerator = lazy(
  () => import("./pages/genres/AfrobeatSongGenerator"),
);
const TrapSongGenerator = lazy(
  () => import("./pages/genres/TrapSongGenerator"),
);
const DrillSongGenerator = lazy(
  () => import("./pages/genres/DrillSongGenerator"),
);
const PhonkSongGenerator = lazy(
  () => import("./pages/genres/PhonkSongGenerator"),
);
const SynthwaveSongGenerator = lazy(
  () => import("./pages/genres/SynthwaveSongGenerator"),
);
const HouseMusicGenerator = lazy(
  () => import("./pages/genres/HouseMusicGenerator"),
);
const TechnoSongGenerator = lazy(
  () => import("./pages/genres/TechnoSongGenerator"),
);
const DubstepSongGenerator = lazy(
  () => import("./pages/genres/DubstepSongGenerator"),
);
const AmbientMusicGenerator = lazy(
  () => import("./pages/genres/AmbientMusicGenerator"),
);
const ChillhopGenerator = lazy(
  () => import("./pages/genres/ChillhopGenerator"),
);
// Occasion pages
const BirthdaySongGenerator = lazy(
  () => import("./pages/genres/BirthdaySongGenerator"),
);
const WeddingSongGenerator = lazy(
  () => import("./pages/genres/WeddingSongGenerator"),
);
const AnniversarySongGenerator = lazy(
  () => import("./pages/genres/AnniversarySongGenerator"),
);
const ValentinesDaySongGenerator = lazy(
  () => import("./pages/genres/ValentinesDaySongGenerator"),
);
const HalloweenSongGenerator = lazy(
  () => import("./pages/genres/HalloweenSongGenerator"),
);
const ThanksgivingSongGenerator = lazy(
  () => import("./pages/genres/ThanksgivingSongGenerator"),
);
const NewYearSongGenerator = lazy(
  () => import("./pages/genres/NewYearSongGenerator"),
);
const GraduationSongGenerator = lazy(
  () => import("./pages/genres/GraduationSongGenerator"),
);
const MothersDaySongGenerator = lazy(
  () => import("./pages/genres/MothersDaySongGenerator"),
);
const FathersDaySongGenerator = lazy(
  () => import("./pages/genres/FathersDaySongGenerator"),
);
const BabyShowerSongGenerator = lazy(
  () => import("./pages/genres/BabyShowerSongGenerator"),
);
// Mood pages
const SadSongGenerator = lazy(() => import("./pages/genres/SadSongGenerator"));
const HappySongGenerator = lazy(
  () => import("./pages/genres/HappySongGenerator"),
);
const ChillSongGenerator = lazy(
  () => import("./pages/genres/ChillSongGenerator"),
);
const MotivationalSongGenerator = lazy(
  () => import("./pages/genres/MotivationalSongGenerator"),
);
const WorkoutSongGenerator = lazy(
  () => import("./pages/genres/WorkoutSongGenerator"),
);
const PartySongGenerator = lazy(
  () => import("./pages/genres/PartySongGenerator"),
);
const RelaxingSongGenerator = lazy(
  () => import("./pages/genres/RelaxingSongGenerator"),
);
const UpbeatSongGenerator = lazy(
  () => import("./pages/genres/UpbeatSongGenerator"),
);
const EmotionalSongGenerator = lazy(
  () => import("./pages/genres/EmotionalSongGenerator"),
);
const MelancholicSongGenerator = lazy(
  () => import("./pages/genres/MelancholicSongGenerator"),
);
const BreakupSongGenerator = lazy(
  () => import("./pages/genres/BreakupSongGenerator"),
);
const HeartbreakSongGenerator = lazy(
  () => import("./pages/genres/HeartbreakSongGenerator"),
);
// Use case pages
const LullabyGenerator = lazy(() => import("./pages/genres/LullabyGenerator"));
const JingleGenerator = lazy(() => import("./pages/genres/JingleGenerator"));
const AnimeSongGenerator = lazy(
  () => import("./pages/genres/AnimeSongGenerator"),
);
const RapBeatGenerator = lazy(() => import("./pages/genres/RapBeatGenerator"));
const ThemeSongGenerator = lazy(
  () => import("./pages/genres/ThemeSongGenerator"),
);
const WorshipSongGenerator = lazy(
  () => import("./pages/genres/WorshipSongGenerator"),
);
const PodcastIntroMusicGenerator = lazy(
  () => import("./pages/genres/PodcastIntroMusicGenerator"),
);
const YoutubeIntroMusicGenerator = lazy(
  () => import("./pages/genres/YoutubeIntroMusicGenerator"),
);
const BackgroundMusicGenerator = lazy(
  () => import("./pages/genres/BackgroundMusicGenerator"),
);
const StudyMusicGenerator = lazy(
  () => import("./pages/genres/StudyMusicGenerator"),
);
const SleepMusicGenerator = lazy(
  () => import("./pages/genres/SleepMusicGenerator"),
);
const MeditationMusicGenerator = lazy(
  () => import("./pages/genres/MeditationMusicGenerator"),
);
const GamingMusicGenerator = lazy(
  () => import("./pages/genres/GamingMusicGenerator"),
);
const RoyaltyFreeMusicGenerator = lazy(
  () => import("./pages/genres/RoyaltyFreeMusicGenerator"),
);
const KidsSongGenerator = lazy(
  () => import("./pages/genres/KidsSongGenerator"),
);
const TiktokSongGenerator = lazy(
  () => import("./pages/genres/TiktokSongGenerator"),
);
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Help = lazy(() => import("./pages/Help"));
const RewardsHelp = lazy(() => import("./pages/RewardsHelp"));
const Support = lazy(() => import("./pages/Support"));
const DownloadApp = lazy(() => import("./pages/DownloadApp"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const AgencyAsaDashboard = lazy(() => import("./pages/AgencyAsaDashboard"));
const Director = lazy(() => import("./pages/Director"));
const DirectorHome = lazy(() => import("./pages/DirectorHome"));
const Projects = lazy(() => import("./pages/Projects"));
const CustomVoice = lazy(() => import("./pages/CustomVoice"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Comparison = lazy(() => import("./pages/Comparison"));
const VersusPage = lazy(() => import("./pages/VersusPage"));
const BestForGenrePage = lazy(() => import("./pages/BestForGenrePage"));
const BpmFinder = lazy(() => import("./pages/BpmFinder"));
const TapTempo = lazy(() => import("./pages/TapTempo"));
const KeyFinder = lazy(() => import("./pages/KeyFinder"));
const ChordProgressionGenerator = lazy(() => import("./pages/ChordProgressionGenerator"));
const SongAnalyzer = lazy(() => import("./pages/SongAnalyzer"));
const AudioJoiner = lazy(() => import("./pages/AudioJoiner"));
const AudioCutter = lazy(() => import("./pages/AudioCutter"));
const ReverseAudio = lazy(() => import("./pages/ReverseAudio"));
const AudioConverter = lazy(() => import("./pages/AudioConverter"));
const VolumeBooster = lazy(() => import("./pages/VolumeBooster"));
const SlowedReverb = lazy(() => import("./pages/SlowedReverb"));
const PitchChanger = lazy(() => import("./pages/PitchChanger"));
const AudioToMidi = lazy(() => import("./pages/AudioToMidi"));
const EightBit = lazy(() => import("./pages/EightBit"));
const Mastering = lazy(() => import("./pages/Mastering"));
const AudioToVideo = lazy(() => import("./pages/AudioToVideo"));
const BestAIMusicVideoGenerator = lazy(
  () => import("./pages/BestAIMusicVideoGenerator"),
);
const AffiliateProgram = lazy(() => import("./pages/AffiliateProgram"));

// Free creator-tools hub — each tool is the same React shell with a different
// toolKey. Translations and SSR body come from shared/freeToolsTranslations.
const FreeToolPage = lazy(() => import("./pages/FreeToolPage"));
// Wrappers for the bare-path /diss-track-generator etc routes.
// Distinct from PR #7's /tools/* page components — same product key, different URL.
const DissTrackGeneratorFree = () => <FreeToolPage toolKey="dissTrack" />;
const AlbumNameGeneratorFree = () => <FreeToolPage toolKey="albumName" />;
const SongTitleGeneratorFree = () => <FreeToolPage toolKey="songTitle" />;
const BandNameGeneratorFree = () => <FreeToolPage toolKey="bandName" />;
const RapLyricsGeneratorFree = () => <FreeToolPage toolKey="rapLyrics" />;
const FreestyleLyricsGeneratorFree = () => (
  <FreeToolPage toolKey="freestyleLyrics" />
);
const HookGeneratorFree = () => <FreeToolPage toolKey="hookGenerator" />;
const DrillLyricsGeneratorFree = () => <FreeToolPage toolKey="drillLyrics" />;
const BratLyricsGeneratorFree = () => <FreeToolPage toolKey="bratLyrics" />;
const BandLogoGeneratorFree = () => <FreeToolPage toolKey="bandLogo" />;
// Second wave (PR for more free creator tools — album cover, audio visualizer,
// artist name, rapper name, K-pop group name).
// Real tool page (AI variants, text editor, 3000 or 4000 px export), not the funnel card.
const AlbumCoverMakerFree = lazy(() => import("./pages/tools/AlbumCoverMaker"));
// Real in-browser visualizer (preview + MP4/WebM export), not a funnel card.
const AudioVisualizerFree = lazy(() => import("./pages/tools/AudioVisualizer"));
const ArtistNameGeneratorFree = () => <FreeToolPage toolKey="artistName" />;
const RapperNameGeneratorFree = () => <FreeToolPage toolKey="rapperName" />;
const KpopGroupNameGeneratorFree = () => (
  <FreeToolPage toolKey="kpopGroupName" />
);
// SEO landing pages targeting high-volume keywords
const AISongGenerator = lazy(() => import("./pages/seo/AISongGenerator"));
const SongMaker = lazy(() => import("./pages/seo/SongMaker"));
const AIMusicMaker = lazy(() => import("./pages/seo/AIMusicMaker"));
const AISongMaker = lazy(() => import("./pages/seo/AISongMaker"));
const AILyricsGenerator = lazy(() => import("./pages/seo/AILyricsGenerator"));
const LyricsToSong = lazy(() => import("./pages/seo/LyricsToSong"));
const TextToSong = lazy(() => import("./pages/seo/TextToSong"));
const AISongwriter = lazy(() => import("./pages/seo/AISongwriter"));

// Tool pages (/tools/*) — SEO landing pages with embedded AI generators
const CreativeTools = lazy(() => import("./pages/CreativeTools"));
const ToolsHub = lazy(() => import("./pages/tools/ToolsHub"));
const ArtistNameGenerator = lazy(
  () => import("./pages/tools/ArtistNameGenerator"),
);
const AlbumNameGenerator = lazy(
  () => import("./pages/tools/AlbumNameGenerator"),
);
const MusicVideoTreatmentGenerator = lazy(
  () => import("./pages/tools/MusicVideoTreatmentGenerator"),
);
const LyricVideoGenerator = lazy(
  () => import("./pages/tools/LyricVideoGenerator"),
);
const SingingVideoGenerator = lazy(
  () => import("./pages/tools/SingingVideoGenerator"),
);
const DanceVideoGenerator = lazy(
  () => import("./pages/tools/DanceVideoGenerator"),
);
const DanceVideoDirectorPage = lazy(
  () => import("./pages/DanceVideoDirectorPage"),
);

// Minimal loading fallback for lazy routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

function LegacyDirectorProjectRedirect() {
  const [location, setLocation] = useLocation();

  useEffect(() => {
    const fullPath =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : location;
    setLocation(
      fullPath.replace("/ai-music-video-generator/", "/ai-music-video/"),
      { replace: true },
    );
  }, [location, setLocation]);

  return <PageLoader />;
}

const SIGNUP_SURVEY_MAX_ACCOUNT_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function LanguageRouter() {
  const [location, setLocation] = useLocation();
  const { i18n } = useTranslation();

  // Reload when a deploy lands mid-session so lazy chunks don't duplicate
  // @trpc/react-query / Radix Contexts against the original Providers.
  useBuildVersionReload(location);

  // Extract language from URL path
  const pathParts = location.split("/").filter(Boolean);
  const potentialLang = pathParts[0];
  const isLangInPath = supportedLanguages.includes(potentialLang as any);
  const currentLang = isLangInPath ? potentialLang : "en";

  // Update i18n language when URL changes
  useEffect(() => {
    if (i18n.language !== currentLang) {
      i18n.changeLanguage(currentLang);
    }
  }, [currentLang, i18n]);

  // Capture paid traffic attribution on every navigation. Click IDs are used
  // by Stripe/Ads conversion uploads; UTM fields are registered as PostHog
  // super properties for signup, checkout, and product-funnel reporting.
  useEffect(() => {
    captureGclidFromUrl();
    syncPostHogAttribution(posthog);
  }, [location]);

  // Redirect /blogs to canonical /blog/
  useEffect(() => {
    if (location === "/blogs" || location === "/blogs/") {
      setLocation("/blog/");
    }
  }, [location, setLocation]);

  const [showSignupSurvey, setShowSignupSurvey] = useState(false);
  const { user } = useAuth();

  // Handle UI side-effects of new OAuth sign-ups (GTM legacy trigger).
  // The PostHog sign_up event itself is fired from useAuth.ts —
  // see SIGN_UP_TRACKED_KEY there — because the `?newSignup=true` URL flag
  // isn't reliable across paywall popups and mobile deep-link auth.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("newSignup") === "true") {
      // Keep the legacy GTM trigger while Google Ads moves to sign_up.
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({ event: "signup_success" });
      // Remove the param from URL without reload
      params.delete("newSignup");
      const newUrl =
        window.location.pathname +
        (params.toString() ? "?" + params.toString() : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  // Show the signup survey once to first-time signups. Works regardless of
  // auth method (the `?newSignup=true` flag only covers OAuth redirects; email
  // OTP signups reload without it). The durable once-per-user gate is
  // `user.signupSurveyAt`, stamped server-side when the modal first opens; the
  // localStorage flags remain as a same-device fast-path.
  useEffect(() => {
    if (import.meta.env.VITE_DISABLE_SIGNUP_SURVEY === "true") return;
    if (!user?.id || !user.createdAt) return;
    // Already saw it on any device/browser — never show again.
    if (user.signupSurveyAt) return;
    // New accounts only: long-standing users who never saw the survey are
    // skipped. The window is a week rather than minutes so people who signed
    // up while the survey was paused still get asked on their next visit.
    const ageMs = Date.now() - new Date(user.createdAt).getTime();
    if (ageMs < 0 || ageMs > SIGNUP_SURVEY_MAX_ACCOUNT_AGE_MS) return;
    try {
      if (
        localStorage.getItem(`signupSurveyCompleted_${user.id}`) === "1" ||
        localStorage.getItem("signupSurveyCompleted") === "1"
      )
        return;
    } catch {}
    setShowSignupSurvey(true);
  }, [user?.id]);

  return (
    <>
      <SignupSurveyModal
        open={showSignupSurvey}
        userId={user?.id}
        onClose={() => setShowSignupSurvey(false)}
      />
      <Switch>
      {/* English routes (no prefix) */}
      {/* The 2026 landing (LandingV2). The previous Landing still serves the
          language-prefixed roots below until v2 is localized. */}
      <Route path="/" component={LandingV2} />
      <Route path="/landing-v2">
        <Redirect to="/" replace />
      </Route>
      <Route path="/download" component={DownloadApp} />
      <Route path="/ai-music-generator" component={Home} />
      {/* SEO landing pages */}
      <Route path="/ai-song-generator" component={AISongGenerator} />
      <Route path="/song-maker" component={SongMaker} />
      <Route path="/ai-music-maker" component={AIMusicMaker} />
      <Route path="/ai-song-maker" component={AISongMaker} />
      <Route path="/ai-lyrics-generator" component={AILyricsGenerator} />
      <Route path="/lyrics-to-song" component={LyricsToSong} />
      <Route path="/text-to-song" component={TextToSong} />
      <Route path="/ai-songwriter" component={AISongwriter} />
      {/* Free tools hub & individual tool pages */}
      <Route path="/creative-tools">{() => <CreativeTools />}</Route>
      <Route path="/creative-tools/:toolId">{() => <CreativeTools />}</Route>
      {/* Creative tools served at their own root URL (creativeToolRootPaths) */}
      <Route path="/ai-video-ad-generator">
        {() => <CreativeTools rootToolId="song-promo-ad" />}
      </Route>
      <Route path="/image-tools">{() => <CreativeTools category="image" />}</Route>
      <Route path="/video-tools">{() => <CreativeTools category="video" />}</Route>
      <Route path="/audio-tools">{() => <CreativeTools category="audio" />}</Route>
      <Route path="/tools" component={ToolsHub} />
      <Route path="/tools/band-name-generator">
        {() => <Redirect to="/band-name-generator" replace />}
      </Route>
      <Route path="/tools/song-name-generator">
        {() => <Redirect to="/song-title-generator" replace />}
      </Route>
      <Route path="/tools/artist-name-generator">
        {() => <Redirect to="/artist-name-generator" replace />}
      </Route>
      <Route path="/tools/album-name-generator">
        {() => <Redirect to="/album-name-generator" replace />}
      </Route>
      <Route
        path="/tools/music-video-treatment-generator"
        component={MusicVideoTreatmentGenerator}
      />
      <Route path="/tools/music-visualizer-free">
        {() => <Redirect to="/audio-visualizer" replace />}
      </Route>
      <Route
        path="/tools/lyric-video-generator"
        component={LyricVideoGenerator}
      />
      <Route
        path="/tools/lyrics-video-generator"
        component={LyricVideoGenerator}
      />
      <Route
        path="/tools/ai-singing-video-generator"
        component={SingingVideoGenerator}
      />
      <Route
        path="/tools/ai-dance-video-generator"
        component={DanceVideoDirectorPage}
      />
      <Route path="/song/:id" component={SongDetail} />
      <Route path="/author/:userId" component={AuthorDetail} />
      <Route path="/notifications" component={Notifications} />
      <Route path="/cli-auth" component={CliAuth} />
      <Route path="/cli" component={CliLanding} />
      <Route path="/skill" component={SkillLanding} />
      <Route path="/mcp-auth" component={McpAuth} />
      <Route path="/mcp" component={McpLanding} />
      <Route path="/voice-cover/:id" component={CoverDetail} />
      <Route path="/video/:id" component={VideoDetail} />
      <Route path="/featured" component={Featured} />
      <Route path="/ai-cover" component={AICover} />
      <Route path="/ai-music-cover" component={AICover} />
      <Route
        path="/ai-cover/custom/:customVoiceId"
        component={VoiceCoverCreate}
      />
      <Route path="/ai-cover/:voiceId" component={VoiceCoverCreate} />
      <Route path="/custom-voice" component={CustomVoice} />
      <Route path="/ai-music-video-generator" component={DirectorHome} />
      {/* SEO cluster pages. Title/body/schema differ server-side via api/_core/seo.ts. */}
      <Route path="/free-ai-music-video-generator" component={DirectorHome} />
      <Route path="/lyric-video-generator" component={LyricVideoGenerator} />
      <Route path="/lyrics-video-generator" component={LyricVideoGenerator} />
      <Route
        path="/ai-singing-video-generator"
        component={SingingVideoGenerator}
      />
      <Route
        path="/singing-video-generator"
        component={SingingVideoGenerator}
      />
      {/* Full dance video product (Director dance mode). The photo-dance
          quick tool lives at /make-photo-dance. */}
      <Route
        path="/ai-dance-video-generator"
        component={DanceVideoDirectorPage}
      />
      <Route
        path="/dance-video-generator"
        component={DanceVideoDirectorPage}
      />
      <Route path="/ai-dance-generator" component={DanceVideoDirectorPage} />
      <Route path="/make-photo-dance" component={DanceVideoGenerator} />
      <Route
        path="/best-ai-music-video-generator"
        component={BestAIMusicVideoGenerator}
      />
      <Route path="/affiliate-program" component={AffiliateProgram} />
      <Route path="/suno-to-video" component={DirectorHome} />
      <Route path="/ai-music-video-director" component={DirectorHome} />
      <Route path="/ai-music-video-workflow" component={DirectorHome} />
      <Route path="/how-to-make-ai-music-video" component={DirectorHome} />
      <Route path="/ai-music-video-app" component={DownloadApp} />
      {/* Free creator-tools hub */}
      <Route
        path="/diss-track-generator"
        component={DissTrackGeneratorFree}
      />
      <Route
        path="/album-name-generator"
        component={AlbumNameGeneratorFree}
      />
      <Route
        path="/song-title-generator"
        component={SongTitleGeneratorFree}
      />
      <Route path="/band-name-generator" component={BandNameGeneratorFree} />
      <Route path="/band-logo-generator" component={BandLogoGeneratorFree} />
      <Route
        path="/freestyle-lyrics-generator"
        component={FreestyleLyricsGeneratorFree}
      />
      <Route path="/hook-generator" component={HookGeneratorFree} />
      <Route
        path="/drill-lyrics-generator"
        component={DrillLyricsGeneratorFree}
      />
      <Route
        path="/brat-lyrics-generator"
        component={BratLyricsGeneratorFree}
      />
      <Route
        path="/rap-lyrics-generator"
        component={RapLyricsGeneratorFree}
      />
      <Route path="/album-cover-maker" component={AlbumCoverMakerFree} />
      <Route path="/audio-visualizer" component={AudioVisualizerFree} />
      <Route
        path="/artist-name-generator"
        component={ArtistNameGeneratorFree}
      />
      <Route
        path="/rapper-name-generator"
        component={RapperNameGeneratorFree}
      />
      <Route
        path="/kpop-group-name-generator"
        component={KpopGroupNameGeneratorFree}
      />
      <Route path="/ai-music-video/:projectId" component={Director} />
      <Route
        path="/ai-music-video-generator/:projectId"
        component={LegacyDirectorProjectRedirect}
      />
      <Route path="/vocal-remover">
        <VocalRemover />
      </Route>
      <Route path="/acapella-extractor">
        <VocalRemover variant="acapella-extractor" />
      </Route>
      <Route path="/instrumental-maker">
        <VocalRemover variant="instrumental-maker" />
      </Route>
      <Route path="/karaoke-maker">
        <VocalRemover variant="karaoke-maker" />
      </Route>
      <Route path="/stem-splitter" component={StemSplitter} />
      <Route path="/songs" component={Songs} />
      <Route path="/projects" component={Projects} />
      <Route path="/pricing" component={Subscription} />
      <Route path="/subscription" component={Subscription} />
      <Route path="/checkout/success" component={CheckoutSuccess} />
      <Route
        path="/christmas-song-generator"
        component={ChristmasSongGenerator}
      />
      <Route
        path="/country-song-generator"
        component={CountrySongGenerator}
      />
      <Route path="/metal-song-generator" component={MetalSongGenerator} />
      <Route path="/love-song-generator" component={LoveSongGenerator} />
      <Route path="/kpop-song-generator" component={KpopSongGenerator} />
      <Route path="/lofi-song-generator" component={LofiSongGenerator} />
      <Route path="/rap-song-generator" component={RapSongGenerator} />
      {/* Genre routes */}
      <Route path="/pop-song-generator" component={PopSongGenerator} />
      <Route path="/rock-song-generator" component={RockSongGenerator} />
      <Route path="/jazz-song-generator" component={JazzSongGenerator} />
      <Route path="/blues-song-generator" component={BluesSongGenerator} />
      <Route path="/rnb-song-generator" component={RnbSongGenerator} />
      <Route path="/hip-hop-song-generator" component={HipHopSongGenerator} />
      <Route path="/edm-song-generator" component={EdmSongGenerator} />
      <Route path="/indie-song-generator" component={IndieSongGenerator} />
      <Route path="/folk-song-generator" component={FolkSongGenerator} />
      <Route path="/reggae-song-generator" component={ReggaeSongGenerator} />
      <Route path="/gospel-song-generator" component={GospelSongGenerator} />
      <Route path="/soul-song-generator" component={SoulSongGenerator} />
      <Route path="/funk-song-generator" component={FunkSongGenerator} />
      <Route path="/punk-song-generator" component={PunkSongGenerator} />
      <Route path="/latin-song-generator" component={LatinSongGenerator} />
      <Route
        path="/afrobeat-song-generator"
        component={AfrobeatSongGenerator}
      />
      <Route path="/trap-song-generator" component={TrapSongGenerator} />
      <Route path="/drill-song-generator" component={DrillSongGenerator} />
      <Route path="/phonk-song-generator" component={PhonkSongGenerator} />
      <Route
        path="/synthwave-song-generator"
        component={SynthwaveSongGenerator}
      />
      <Route path="/house-music-generator" component={HouseMusicGenerator} />
      <Route path="/techno-song-generator" component={TechnoSongGenerator} />
      <Route
        path="/dubstep-song-generator"
        component={DubstepSongGenerator}
      />
      <Route
        path="/ambient-music-generator"
        component={AmbientMusicGenerator}
      />
      <Route path="/chillhop-generator" component={ChillhopGenerator} />
      {/* Occasion routes */}
      <Route
        path="/birthday-song-generator"
        component={BirthdaySongGenerator}
      />
      <Route
        path="/wedding-song-generator"
        component={WeddingSongGenerator}
      />
      <Route
        path="/anniversary-song-generator"
        component={AnniversarySongGenerator}
      />
      <Route
        path="/valentines-day-song-generator"
        component={ValentinesDaySongGenerator}
      />
      <Route
        path="/valentines-song-generator"
        component={ValentinesDaySongGenerator}
      />
      <Route
        path="/halloween-song-generator"
        component={HalloweenSongGenerator}
      />
      <Route
        path="/thanksgiving-song-generator"
        component={ThanksgivingSongGenerator}
      />
      <Route
        path="/new-year-song-generator"
        component={NewYearSongGenerator}
      />
      <Route
        path="/newyear-song-generator"
        component={NewYearSongGenerator}
      />
      <Route
        path="/graduation-song-generator"
        component={GraduationSongGenerator}
      />
      <Route
        path="/mothers-day-song-generator"
        component={MothersDaySongGenerator}
      />
      <Route
        path="/mothersday-song-generator"
        component={MothersDaySongGenerator}
      />
      <Route
        path="/fathers-day-song-generator"
        component={FathersDaySongGenerator}
      />
      <Route
        path="/fathersday-song-generator"
        component={FathersDaySongGenerator}
      />
      <Route
        path="/baby-shower-song-generator"
        component={BabyShowerSongGenerator}
      />
      {/* Mood routes */}
      <Route path="/sad-song-generator" component={SadSongGenerator} />
      <Route path="/happy-song-generator" component={HappySongGenerator} />
      <Route path="/chill-song-generator" component={ChillSongGenerator} />
      <Route
        path="/motivational-song-generator"
        component={MotivationalSongGenerator}
      />
      <Route
        path="/workout-song-generator"
        component={WorkoutSongGenerator}
      />
      <Route path="/party-song-generator" component={PartySongGenerator} />
      <Route
        path="/relaxing-song-generator"
        component={RelaxingSongGenerator}
      />
      <Route path="/upbeat-song-generator" component={UpbeatSongGenerator} />
      <Route
        path="/emotional-song-generator"
        component={EmotionalSongGenerator}
      />
      <Route
        path="/melancholic-song-generator"
        component={MelancholicSongGenerator}
      />
      <Route
        path="/breakup-song-generator"
        component={BreakupSongGenerator}
      />
      <Route
        path="/heartbreak-song-generator"
        component={HeartbreakSongGenerator}
      />
      {/* Use case routes */}
      <Route path="/lullaby-generator" component={LullabyGenerator} />
      <Route path="/jingle-generator" component={JingleGenerator} />
      <Route path="/anime-song-generator" component={AnimeSongGenerator} />
      <Route path="/rap-beat-generator" component={RapBeatGenerator} />
      <Route path="/theme-song-generator" component={ThemeSongGenerator} />
      <Route
        path="/worship-song-generator"
        component={WorshipSongGenerator}
      />
      <Route
        path="/podcast-intro-music-generator"
        component={PodcastIntroMusicGenerator}
      />
      <Route
        path="/podcast-intro-song-generator"
        component={PodcastIntroMusicGenerator}
      />
      <Route
        path="/youtube-intro-music-generator"
        component={YoutubeIntroMusicGenerator}
      />
      <Route
        path="/youtube-intro-song-generator"
        component={YoutubeIntroMusicGenerator}
      />
      <Route
        path="/background-music-generator"
        component={BackgroundMusicGenerator}
      />
      <Route path="/study-music-generator" component={StudyMusicGenerator} />
      <Route path="/sleep-music-generator" component={SleepMusicGenerator} />
      <Route
        path="/meditation-music-generator"
        component={MeditationMusicGenerator}
      />
      <Route
        path="/gaming-music-generator"
        component={GamingMusicGenerator}
      />
      <Route
        path="/royalty-free-music-generator"
        component={RoyaltyFreeMusicGenerator}
      />
      <Route path="/kids-song-generator" component={KidsSongGenerator} />
      <Route path="/tiktok-song-generator" component={TiktokSongGenerator} />
      <Route path="/vibes" component={Home} />
      <Route path="/playlists" component={Playlists} />
      <Route path="/playlist/:id" component={PlaylistDetail} />
      <Route path="/terms" component={Terms} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/about" component={About} />
      <Route path="/contact" component={Contact} />
      <Route path="/help" component={Help} />
      <Route path="/rewards-help" component={RewardsHelp} />
      <Route path="/support" component={Support} />
      <Route path="/delete-account" component={DeleteAccount} />
      <Route path="/unsubscribe" component={Unsubscribe} />
      <Route path="/blog" component={Blog} />
      <Route path="/blog/" component={Blog} />
      <Route path="/blog/:slug" component={BlogPost} />
      <Route path="/blogs" component={Blog} />
      <Route path="/blogs/" component={Blog} />
      <Route path="/comparison" component={Comparison} />
      <Route path="/vs/:slug" component={VersusPage} />
      <Route path="/best-for/:slug" component={BestForGenrePage} />
      <Route path="/bpm-finder" component={BpmFinder} />
      <Route path="/tap-tempo" component={TapTempo} />
      <Route path="/key-finder" component={KeyFinder} />
      <Route path="/chord-progression-generator" component={ChordProgressionGenerator} />
      <Route path="/song-analyzer" component={SongAnalyzer} />
      <Route path="/audio-joiner" component={AudioJoiner} />
      <Route path="/audio-cutter" component={AudioCutter} />
      <Route path="/reverse-audio" component={ReverseAudio} />
      <Route path="/audio-converter" component={AudioConverter} />
      <Route path="/m4a-to-mp3" component={AudioConverter} />
      <Route path="/wav-to-mp3" component={AudioConverter} />
      <Route path="/mp3-to-wav" component={AudioConverter} />
      <Route path="/ogg-to-mp3" component={AudioConverter} />
      <Route path="/flac-to-mp3" component={AudioConverter} />
      <Route path="/opus-to-mp3" component={AudioConverter} />
      <Route path="/aac-to-mp3" component={AudioConverter} />
      <Route path="/mp4-to-mp3" component={AudioConverter} />
      <Route path="/mov-to-mp3" component={AudioConverter} />
      <Route path="/video-to-mp3" component={AudioConverter} />
      <Route path="/video-to-audio" component={AudioConverter} />
      <Route path="/volume-booster" component={VolumeBooster} />
      <Route path="/slowed-reverb" component={SlowedReverb} />
      <Route path="/pitch-changer" component={PitchChanger} />
      <Route path="/audio-to-midi" component={AudioToMidi} />
      <Route path="/8-bit-converter" component={EightBit} />
      <Route path="/audio-mastering" component={Mastering} />
      <Route path="/spotify-canvas-maker">
        {() => <Redirect to="/creative-tools/spotify-canvas-maker" replace />}
      </Route>
      <Route path="/mp3-to-mp4" component={AudioToVideo} />
      <Route path="/audio-to-video" component={AudioToVideo} />
      <Route path="/wav-to-mp4" component={AudioToVideo} />
      <Route path="/m4a-to-mp4" component={AudioToVideo} />
      <Route path="/admin" component={AdminDashboard} />
      <Route path="/admin/support" component={AdminSupport} />
      <Route path="/agency/asa" component={AgencyAsaDashboard} />

      {/* Language-prefixed routes. The bare /:lang route must only match real
          language codes — otherwise any unknown single-segment path (/foo,
          /index.html, stale marketing slugs) soft-404s into the homepage, and
          because Layout only hides its chrome for known landing paths, the
          site header+sidebar render stacked on top of Landing's own nav. */}
      <Route path="/:lang">
        {(params) =>
          supportedLanguages.includes(
            params.lang as (typeof supportedLanguages)[number],
          ) ? (
            <LandingV2 />
          ) : (
            <NotFound />
          )
        }
      </Route>
      <Route path="/:lang/download" component={DownloadApp} />
      <Route path="/:lang/ai-music-generator" component={Home} />
      {/* SEO landing pages (language-prefixed) */}
      <Route path="/:lang/ai-song-generator" component={AISongGenerator} />
      <Route path="/:lang/song-maker" component={SongMaker} />
      <Route path="/:lang/ai-music-maker" component={AIMusicMaker} />
      <Route path="/:lang/ai-song-maker" component={AISongMaker} />
      <Route
        path="/:lang/ai-lyrics-generator"
        component={AILyricsGenerator}
      />
      <Route path="/:lang/lyrics-to-song" component={LyricsToSong} />
      <Route path="/:lang/text-to-song" component={TextToSong} />
      <Route path="/:lang/ai-songwriter" component={AISongwriter} />
      {/* Free tools hub & individual tool pages (i18n) */}
      <Route path="/:lang/creative-tools">{() => <CreativeTools />}</Route>
      <Route path="/:lang/creative-tools/:toolId">{() => <CreativeTools />}</Route>
      <Route path="/:lang/ai-video-ad-generator">
        {() => <CreativeTools rootToolId="song-promo-ad" />}
      </Route>
      <Route path="/:lang/image-tools">{() => <CreativeTools category="image" />}</Route>
      <Route path="/:lang/video-tools">{() => <CreativeTools category="video" />}</Route>
      <Route path="/:lang/audio-tools">{() => <CreativeTools category="audio" />}</Route>
      <Route path="/:lang/tools" component={ToolsHub} />
      <Route path="/:lang/tools/band-name-generator">
        {(params) => (
          <Redirect to={`/${params.lang}/band-name-generator`} replace />
        )}
      </Route>
      <Route path="/:lang/tools/song-name-generator">
        {(params) => (
          <Redirect to={`/${params.lang}/song-title-generator`} replace />
        )}
      </Route>
      <Route path="/:lang/tools/artist-name-generator">
        {(params) => (
          <Redirect to={`/${params.lang}/artist-name-generator`} replace />
        )}
      </Route>
      <Route path="/:lang/tools/album-name-generator">
        {(params) => (
          <Redirect to={`/${params.lang}/album-name-generator`} replace />
        )}
      </Route>
      <Route
        path="/:lang/tools/music-video-treatment-generator"
        component={MusicVideoTreatmentGenerator}
      />
      <Route path="/:lang/tools/music-visualizer-free">
        {(params) => (
          <Redirect to={`/${params.lang}/audio-visualizer`} replace />
        )}
      </Route>
      <Route
        path="/:lang/tools/lyric-video-generator"
        component={LyricVideoGenerator}
      />
      <Route
        path="/:lang/tools/lyrics-video-generator"
        component={LyricVideoGenerator}
      />
      <Route
        path="/:lang/tools/ai-singing-video-generator"
        component={SingingVideoGenerator}
      />
      <Route
        path="/:lang/tools/ai-dance-video-generator"
        component={DanceVideoDirectorPage}
      />
      <Route path="/:lang/song/:id" component={SongDetail} />
      <Route path="/:lang/author/:userId" component={AuthorDetail} />
      <Route path="/:lang/notifications" component={Notifications} />
      <Route path="/:lang/voice-cover/:id" component={CoverDetail} />
      <Route path="/:lang/video/:id" component={VideoDetail} />
      <Route path="/:lang/featured" component={Featured} />
      <Route path="/:lang/ai-cover" component={AICover} />
      <Route path="/:lang/ai-music-cover" component={AICover} />
      <Route
        path="/:lang/ai-cover/custom/:customVoiceId"
        component={VoiceCoverCreate}
      />
      <Route path="/:lang/ai-cover/:voiceId" component={VoiceCoverCreate} />
      <Route path="/:lang/custom-voice" component={CustomVoice} />
      <Route
        path="/:lang/ai-music-video-generator"
        component={DirectorHome}
      />
      <Route
        path="/:lang/free-ai-music-video-generator"
        component={DirectorHome}
      />
      <Route
        path="/:lang/lyric-video-generator"
        component={LyricVideoGenerator}
      />
      <Route
        path="/:lang/lyrics-video-generator"
        component={LyricVideoGenerator}
      />
      <Route
        path="/:lang/ai-singing-video-generator"
        component={SingingVideoGenerator}
      />
      <Route
        path="/:lang/singing-video-generator"
        component={SingingVideoGenerator}
      />
      <Route
        path="/:lang/ai-dance-video-generator"
        component={DanceVideoDirectorPage}
      />
      <Route
        path="/:lang/dance-video-generator"
        component={DanceVideoDirectorPage}
      />
      <Route
        path="/:lang/ai-dance-generator"
        component={DanceVideoDirectorPage}
      />
      <Route path="/:lang/make-photo-dance" component={DanceVideoGenerator} />
      <Route
        path="/:lang/best-ai-music-video-generator"
        component={BestAIMusicVideoGenerator}
      />
      <Route path="/:lang/affiliate-program" component={AffiliateProgram} />
      <Route path="/:lang/suno-to-video" component={DirectorHome} />
      <Route path="/:lang/ai-music-video-director" component={DirectorHome} />
      <Route path="/:lang/ai-music-video-workflow" component={DirectorHome} />
      <Route path="/:lang/how-to-make-ai-music-video" component={DirectorHome} />
      <Route path="/:lang/ai-music-video-app" component={DownloadApp} />
      <Route
        path="/:lang/diss-track-generator"
        component={DissTrackGeneratorFree}
      />
      <Route
        path="/:lang/album-name-generator"
        component={AlbumNameGeneratorFree}
      />
      <Route
        path="/:lang/song-title-generator"
        component={SongTitleGeneratorFree}
      />
      <Route
        path="/:lang/band-name-generator"
        component={BandNameGeneratorFree}
      />
      <Route
        path="/:lang/band-logo-generator"
        component={BandLogoGeneratorFree}
      />
      <Route
        path="/:lang/freestyle-lyrics-generator"
        component={FreestyleLyricsGeneratorFree}
      />
      <Route
        path="/:lang/hook-generator"
        component={HookGeneratorFree}
      />
      <Route
        path="/:lang/drill-lyrics-generator"
        component={DrillLyricsGeneratorFree}
      />
      <Route
        path="/:lang/brat-lyrics-generator"
        component={BratLyricsGeneratorFree}
      />
      <Route
        path="/:lang/rap-lyrics-generator"
        component={RapLyricsGeneratorFree}
      />
      <Route
        path="/:lang/album-cover-maker"
        component={AlbumCoverMakerFree}
      />
      <Route path="/:lang/audio-visualizer" component={AudioVisualizerFree} />
      <Route
        path="/:lang/artist-name-generator"
        component={ArtistNameGeneratorFree}
      />
      <Route
        path="/:lang/rapper-name-generator"
        component={RapperNameGeneratorFree}
      />
      <Route
        path="/:lang/kpop-group-name-generator"
        component={KpopGroupNameGeneratorFree}
      />
      <Route path="/:lang/ai-music-video/:projectId" component={Director} />
      <Route
        path="/:lang/ai-music-video-generator/:projectId"
        component={LegacyDirectorProjectRedirect}
      />
      <Route path="/:lang/vocal-remover">
        <VocalRemover />
      </Route>
      <Route path="/:lang/acapella-extractor">
        <VocalRemover variant="acapella-extractor" />
      </Route>
      <Route path="/:lang/instrumental-maker">
        <VocalRemover variant="instrumental-maker" />
      </Route>
      <Route path="/:lang/karaoke-maker">
        <VocalRemover variant="karaoke-maker" />
      </Route>
      <Route path="/:lang/stem-splitter" component={StemSplitter} />
      <Route path="/:lang/songs" component={Songs} />
      <Route path="/:lang/projects" component={Projects} />
      <Route path="/:lang/pricing" component={Subscription} />
      <Route path="/:lang/subscription" component={Subscription} />
      <Route path="/:lang/checkout/success" component={CheckoutSuccess} />
      <Route
        path="/:lang/christmas-song-generator"
        component={ChristmasSongGenerator}
      />
      <Route
        path="/:lang/country-song-generator"
        component={CountrySongGenerator}
      />
      <Route
        path="/:lang/metal-song-generator"
        component={MetalSongGenerator}
      />
      <Route
        path="/:lang/love-song-generator"
        component={LoveSongGenerator}
      />
      <Route
        path="/:lang/kpop-song-generator"
        component={KpopSongGenerator}
      />
      <Route
        path="/:lang/lofi-song-generator"
        component={LofiSongGenerator}
      />
      <Route path="/:lang/rap-song-generator" component={RapSongGenerator} />
      {/* Genre routes */}
      <Route path="/:lang/pop-song-generator" component={PopSongGenerator} />
      <Route
        path="/:lang/rock-song-generator"
        component={RockSongGenerator}
      />
      <Route
        path="/:lang/jazz-song-generator"
        component={JazzSongGenerator}
      />
      <Route
        path="/:lang/blues-song-generator"
        component={BluesSongGenerator}
      />
      <Route path="/:lang/rnb-song-generator" component={RnbSongGenerator} />
      <Route
        path="/:lang/hip-hop-song-generator"
        component={HipHopSongGenerator}
      />
      <Route path="/:lang/edm-song-generator" component={EdmSongGenerator} />
      <Route
        path="/:lang/indie-song-generator"
        component={IndieSongGenerator}
      />
      <Route
        path="/:lang/folk-song-generator"
        component={FolkSongGenerator}
      />
      <Route
        path="/:lang/reggae-song-generator"
        component={ReggaeSongGenerator}
      />
      <Route
        path="/:lang/gospel-song-generator"
        component={GospelSongGenerator}
      />
      <Route
        path="/:lang/soul-song-generator"
        component={SoulSongGenerator}
      />
      <Route
        path="/:lang/funk-song-generator"
        component={FunkSongGenerator}
      />
      <Route
        path="/:lang/punk-song-generator"
        component={PunkSongGenerator}
      />
      <Route
        path="/:lang/latin-song-generator"
        component={LatinSongGenerator}
      />
      <Route
        path="/:lang/afrobeat-song-generator"
        component={AfrobeatSongGenerator}
      />
      <Route
        path="/:lang/trap-song-generator"
        component={TrapSongGenerator}
      />
      <Route
        path="/:lang/drill-song-generator"
        component={DrillSongGenerator}
      />
      <Route
        path="/:lang/phonk-song-generator"
        component={PhonkSongGenerator}
      />
      <Route
        path="/:lang/synthwave-song-generator"
        component={SynthwaveSongGenerator}
      />
      <Route
        path="/:lang/house-music-generator"
        component={HouseMusicGenerator}
      />
      <Route
        path="/:lang/techno-song-generator"
        component={TechnoSongGenerator}
      />
      <Route
        path="/:lang/dubstep-song-generator"
        component={DubstepSongGenerator}
      />
      <Route
        path="/:lang/ambient-music-generator"
        component={AmbientMusicGenerator}
      />
      <Route path="/:lang/chillhop-generator" component={ChillhopGenerator} />
      {/* Occasion routes */}
      <Route
        path="/:lang/birthday-song-generator"
        component={BirthdaySongGenerator}
      />
      <Route
        path="/:lang/wedding-song-generator"
        component={WeddingSongGenerator}
      />
      <Route
        path="/:lang/anniversary-song-generator"
        component={AnniversarySongGenerator}
      />
      <Route
        path="/:lang/valentines-day-song-generator"
        component={ValentinesDaySongGenerator}
      />
      <Route
        path="/:lang/valentines-song-generator"
        component={ValentinesDaySongGenerator}
      />
      <Route
        path="/:lang/halloween-song-generator"
        component={HalloweenSongGenerator}
      />
      <Route
        path="/:lang/thanksgiving-song-generator"
        component={ThanksgivingSongGenerator}
      />
      <Route
        path="/:lang/new-year-song-generator"
        component={NewYearSongGenerator}
      />
      <Route
        path="/:lang/newyear-song-generator"
        component={NewYearSongGenerator}
      />
      <Route
        path="/:lang/graduation-song-generator"
        component={GraduationSongGenerator}
      />
      <Route
        path="/:lang/mothers-day-song-generator"
        component={MothersDaySongGenerator}
      />
      <Route
        path="/:lang/mothersday-song-generator"
        component={MothersDaySongGenerator}
      />
      <Route
        path="/:lang/fathers-day-song-generator"
        component={FathersDaySongGenerator}
      />
      <Route
        path="/:lang/fathersday-song-generator"
        component={FathersDaySongGenerator}
      />
      <Route
        path="/:lang/baby-shower-song-generator"
        component={BabyShowerSongGenerator}
      />
      {/* Mood routes */}
      <Route path="/:lang/sad-song-generator" component={SadSongGenerator} />
      <Route
        path="/:lang/happy-song-generator"
        component={HappySongGenerator}
      />
      <Route
        path="/:lang/chill-song-generator"
        component={ChillSongGenerator}
      />
      <Route
        path="/:lang/motivational-song-generator"
        component={MotivationalSongGenerator}
      />
      <Route
        path="/:lang/workout-song-generator"
        component={WorkoutSongGenerator}
      />
      <Route
        path="/:lang/party-song-generator"
        component={PartySongGenerator}
      />
      <Route
        path="/:lang/relaxing-song-generator"
        component={RelaxingSongGenerator}
      />
      <Route
        path="/:lang/upbeat-song-generator"
        component={UpbeatSongGenerator}
      />
      <Route
        path="/:lang/emotional-song-generator"
        component={EmotionalSongGenerator}
      />
      <Route
        path="/:lang/melancholic-song-generator"
        component={MelancholicSongGenerator}
      />
      <Route
        path="/:lang/breakup-song-generator"
        component={BreakupSongGenerator}
      />
      <Route
        path="/:lang/heartbreak-song-generator"
        component={HeartbreakSongGenerator}
      />
      {/* Use case routes */}
      <Route path="/:lang/lullaby-generator" component={LullabyGenerator} />
      <Route path="/:lang/jingle-generator" component={JingleGenerator} />
      <Route
        path="/:lang/anime-song-generator"
        component={AnimeSongGenerator}
      />
      <Route path="/:lang/rap-beat-generator" component={RapBeatGenerator} />
      <Route
        path="/:lang/theme-song-generator"
        component={ThemeSongGenerator}
      />
      <Route
        path="/:lang/worship-song-generator"
        component={WorshipSongGenerator}
      />
      <Route
        path="/:lang/podcast-intro-music-generator"
        component={PodcastIntroMusicGenerator}
      />
      <Route
        path="/:lang/podcast-intro-song-generator"
        component={PodcastIntroMusicGenerator}
      />
      <Route
        path="/:lang/youtube-intro-music-generator"
        component={YoutubeIntroMusicGenerator}
      />
      <Route
        path="/:lang/youtube-intro-song-generator"
        component={YoutubeIntroMusicGenerator}
      />
      <Route
        path="/:lang/background-music-generator"
        component={BackgroundMusicGenerator}
      />
      <Route
        path="/:lang/study-music-generator"
        component={StudyMusicGenerator}
      />
      <Route
        path="/:lang/sleep-music-generator"
        component={SleepMusicGenerator}
      />
      <Route
        path="/:lang/meditation-music-generator"
        component={MeditationMusicGenerator}
      />
      <Route
        path="/:lang/gaming-music-generator"
        component={GamingMusicGenerator}
      />
      <Route
        path="/:lang/royalty-free-music-generator"
        component={RoyaltyFreeMusicGenerator}
      />
      <Route
        path="/:lang/kids-song-generator"
        component={KidsSongGenerator}
      />
      <Route
        path="/:lang/tiktok-song-generator"
        component={TiktokSongGenerator}
      />
      <Route path="/:lang/vibes" component={Home} />
      <Route path="/:lang/playlists" component={Playlists} />
      <Route path="/:lang/playlist/:id" component={PlaylistDetail} />
      <Route path="/:lang/terms" component={Terms} />
      <Route path="/:lang/privacy" component={Privacy} />
      <Route path="/:lang/about" component={About} />
      <Route path="/:lang/contact" component={Contact} />
      <Route path="/:lang/help" component={Help} />
      <Route path="/:lang/mcp" component={McpLanding} />
      <Route path="/:lang/rewards-help" component={RewardsHelp} />
      <Route path="/:lang/support" component={Support} />
      <Route path="/:lang/delete-account" component={DeleteAccount} />
      <Route path="/:lang/blog" component={Blog} />
      <Route path="/:lang/blog/" component={Blog} />
      <Route path="/:lang/blog/:slug" component={BlogPost} />
      <Route path="/:lang/blogs" component={Blog} />
      <Route path="/:lang/blogs/" component={Blog} />
      <Route path="/:lang/comparison" component={Comparison} />
      <Route path="/:lang/vs/:slug" component={VersusPage} />
      <Route path="/:lang/best-for/:slug" component={BestForGenrePage} />
      <Route path="/:lang/bpm-finder" component={BpmFinder} />
      <Route path="/:lang/tap-tempo" component={TapTempo} />
      <Route path="/:lang/key-finder" component={KeyFinder} />
      <Route path="/:lang/chord-progression-generator" component={ChordProgressionGenerator} />
      <Route path="/:lang/song-analyzer" component={SongAnalyzer} />
      <Route path="/:lang/audio-joiner" component={AudioJoiner} />
      <Route path="/:lang/audio-cutter" component={AudioCutter} />
      <Route path="/:lang/reverse-audio" component={ReverseAudio} />
      <Route path="/:lang/audio-converter" component={AudioConverter} />
      <Route path="/:lang/m4a-to-mp3" component={AudioConverter} />
      <Route path="/:lang/wav-to-mp3" component={AudioConverter} />
      <Route path="/:lang/mp3-to-wav" component={AudioConverter} />
      <Route path="/:lang/ogg-to-mp3" component={AudioConverter} />
      <Route path="/:lang/flac-to-mp3" component={AudioConverter} />
      <Route path="/:lang/opus-to-mp3" component={AudioConverter} />
      <Route path="/:lang/aac-to-mp3" component={AudioConverter} />
      <Route path="/:lang/mp4-to-mp3" component={AudioConverter} />
      <Route path="/:lang/mov-to-mp3" component={AudioConverter} />
      <Route path="/:lang/video-to-mp3" component={AudioConverter} />
      <Route path="/:lang/video-to-audio" component={AudioConverter} />
      <Route path="/:lang/volume-booster" component={VolumeBooster} />
      <Route path="/:lang/slowed-reverb" component={SlowedReverb} />
      <Route path="/:lang/pitch-changer" component={PitchChanger} />
      <Route path="/:lang/audio-to-midi" component={AudioToMidi} />
      <Route path="/:lang/8-bit-converter" component={EightBit} />
      <Route path="/:lang/audio-mastering" component={Mastering} />
      <Route path="/:lang/spotify-canvas-maker">
        {(params) => (
          <Redirect
            to={`/${params.lang}/creative-tools/spotify-canvas-maker`}
            replace
          />
        )}
      </Route>
      <Route path="/:lang/mp3-to-mp4" component={AudioToVideo} />
      <Route path="/:lang/audio-to-video" component={AudioToVideo} />
      <Route path="/:lang/wav-to-mp4" component={AudioToVideo} />
      <Route path="/:lang/m4a-to-mp4" component={AudioToVideo} />
      <Route path="/:lang/admin" component={AdminDashboard} />
      <Route path="/:lang/admin/support" component={AdminSupport} />

      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
      </Switch>
    </>
  );
}

function Router() {
  return (
    <Layout>
      <ImpersonationBanner />
      <Suspense fallback={<PageLoader />}>
        <LanguageRouter />
      </Suspense>
    </Layout>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="dark"
        // switchable
      >
        <AudioPlayerProvider>
          <TooltipProvider>
            <Toaster />
            <MobileAppPrompt />
            <Router />
            <StarterCreditsWelcome />
            <GlobalMusicPlayer />
          </TooltipProvider>
        </AudioPlayerProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
